import {Component} from '@wonderlandengine/api';
import {BooleanName} from '../enums/BooleanName.js';
import {Statistics} from './statistics.js';
import {StatNames} from '../enums/StatNames.js';

/**
 * Stores boolean flags using bitfields for efficient storage
 */
export class BooleanStore extends Component {
    static TypeName = 'boolean-store';

    // Singleton
    private static _instance: BooleanStore | null = null;

    static get instance(): BooleanStore {
        if (!BooleanStore._instance) {
            throw new Error('BooleanStore: Instance not created yet');
        }
        return BooleanStore._instance;
    }

    // JavaScript bitwise operations are limited to 32 bits
    private static readonly _BITS_PER_FIELD = 32;

    init(): void {
        if (BooleanStore._instance) {
            throw new Error(
                'BooleanStore: There can only be one instance of BooleanStore Component'
            );
        }
        BooleanStore._instance = this;
    }

    /**
     * Sets a boolean flag by name
     * @param name The flag to set
     * @param value The boolean value to store
     */
    set(name: BooleanName, value: boolean): void {
        const fieldIndex = Math.floor(Number(name) / BooleanStore._BITS_PER_FIELD);
        const bitPosition = Number(name) % BooleanStore._BITS_PER_FIELD;
        const statName = this._getStatNameForField(fieldIndex);

        let bitfield = Statistics.instance.getLong(statName);
        bitfield = this._write(bitfield, bitPosition, value);
        Statistics.instance.record(statName, bitfield);
    }

    /**
     * Gets a boolean flag by name
     * @param name The flag to get
     * @returns The stored boolean value
     */
    get(name: BooleanName): boolean {
        const fieldIndex = Math.floor(Number(name) / BooleanStore._BITS_PER_FIELD);
        const bitPosition = Number(name) % BooleanStore._BITS_PER_FIELD;
        const statName = this._getStatNameForField(fieldIndex);

        const bitfield = Statistics.instance.getLong(statName);
        return this._read(bitfield, bitPosition);
    }

    /**
     * Maps a field index to the corresponding stat name
     * @param fieldIndex The field index
     * @returns The stat name for storage
     */
    private _getStatNameForField(fieldIndex: number): StatNames {
        switch (fieldIndex) {
            case 0:
                return StatNames.BooleanStore;
            case 1:
                return StatNames.BooleanStore2;
            case 2:
                return StatNames.BooleanStore3;
            case 3:
                return StatNames.BooleanStore4;
            case 4:
                return StatNames.BooleanStore5;
            // Add more cases if needed for additional fields
            default:
                throw new Error(
                    `BooleanStore: Invalid field index ${fieldIndex}. Max supported boolean index is ${2 * BooleanStore._BITS_PER_FIELD - 1}`
                );
        }
    }

    /**
     * Writes a boolean value to a specific bit in a bitfield
     * @param field The bitfield to modify
     * @param bit The bit position to modify
     * @param value The boolean value to write
     * @returns The modified bitfield
     */
    private _write(field: number, bit: number, value: boolean): number {
        const mask = 1 << bit;
        if (value) {
            field |= mask;
        } else {
            field &= ~mask;
        }
        return field;
    }

    /**
     * Reads a boolean value from a specific bit in a bitfield
     * @param bitfield The bitfield to read from
     * @param bit The bit position to read
     * @returns The boolean value
     */
    private _read(bitfield: number, bit: number): boolean {
        const mask = 1 << bit;
        return (bitfield & mask) > 0;
    }
}
